import { toRem, baseVariables, Button } from '@marriott/mi-ui-library';
import styled, { css } from 'styled-components';
import { IconWrapper } from './Amenity.styles';

type EnrollmentCardProps = {
  $isLoggedInVariant?: boolean;
  $theme?: string;
};

const isLoggedInVariant = (loggedInCase: string | number, loggedOutCase: string | number) => {
  return ({ $isLoggedInVariant }: EnrollmentCardProps) => ($isLoggedInVariant ? loggedInCase : loggedOutCase);
};

export const Description = styled.p.attrs(({ $isLoggedInVariant }: EnrollmentCardProps) => ({
  className: `t-font-${$isLoggedInVariant ? 'inverse-s' : 's'}`,
}))`
  color: ${baseVariables.color.base20};
  ${({ $isLoggedInVariant }) =>
    $isLoggedInVariant &&
    css`
      margin-bottom: ${toRem(16)} !important;
    `}
  display: ${isLoggedInVariant('block', 'none')};

  @media ${baseVariables.mediaQuery.lg} {
    display: block;
  }
`;

export const Title = styled.div.attrs(({ $isLoggedInVariant }: EnrollmentCardProps) => ({
  className: `t-title-${$isLoggedInVariant ? 'inverse-s' : 's'}`,
  role: 'heading',
  'aria-level': 2,
}))`
  margin-bottom: ${isLoggedInVariant(toRem(8), toRem(23))} !important;

  @media ${baseVariables.mediaQuery.lg} {
    margin-bottom: ${isLoggedInVariant(toRem(8), toRem(12))} !important;
  }
`;

export const CenterContentGrid = styled.div<EnrollmentCardProps>`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, auto);
  margin-bottom: ${toRem(23)};
  flex: 1;

  & > *:nth-child(1),
  & > *:nth-child(3) {
    border-right: 1px solid
      ${({ $theme }) => ($theme === 'inverse' ? 'rgba(255, 255, 255, 0.20)' : baseVariables.color.neutral20)}; //rgb for base 25
  }

  & > *:nth-child(1),
  & > *:nth-child(2) {
    border-bottom: 1px solid
      ${({ $theme }) => ($theme === 'inverse' ? 'rgba(255, 255, 255, 0.20)' : baseVariables.color.neutral20)}; //rgb for base 25
  }

  @media ${baseVariables.mediaQuery.lg} {
    margin-bottom: ${toRem(26)};
  }
`;

export const ButtonRow = styled.div`
  display: flex;
  justify-content: center;
  gap: ${toRem(24)};
`;

export const StyledButton = styled(Button)`
  flex: 1;
`;

export const EnrollmentCardContent = styled.div`
  z-index: 0; // sets base z-index, so children don't fall through when they set their own z-index.
  position: relative;
  line-height: 150%;
  display: flex;
  flex-direction: column;
  padding: ${toRem(24)};
  width: 100%;

  @media ${baseVariables.mediaQuery.xl} {
    padding: ${toRem(40)};
  }
`;

export const FloatingCardContent = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  border-radius: 14px;
  overflow: hidden;
`;

export const Card = styled.div<EnrollmentCardProps>`
  position: relative;
  margin: 0 auto;
  border-radius: ${toRem(14)};
  background: ${baseVariables.color.base20};
  text-align: center;
  width: 100%;
  aspect-ratio: 1/1;
  overflow: hidden;

  &:not(.logged-in-variant) {
    ${Description} {
      color: ${
        ({ $theme }) => ($theme === 'inverse' ? baseVariables.color.neutral30 : baseVariables.color.neutral40) // moved this style here to avoid !important;
      };
    }
  }

  img {
    transition: transform 600ms ease;
  }

  .enrollment-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    border-radius: ${toRem(14)};
  }

  ${Title}, ${CenterContentGrid} {
    margin-bottom: ${toRem(23)};
  }

  @media ${baseVariables.mediaQuery.md} {
    ${Title}, ${CenterContentGrid} {
      margin-bottom: ${toRem(18)};
    }
  }

  @media ${baseVariables.mediaQuery.lg} {
    ${Title} {
      margin-bottom: ${toRem(10)};
    }
    ${Description}, ${CenterContentGrid} {
      margin-bottom: ${toRem(25.5)};
    }
  }

  ${IconWrapper} {
    transition: transform 600ms ease-in-out;
    transform-origin: center center;
  }

  @media not all and (hover: none) {
    &:not(.page-loading):hover {
      ${IconWrapper} {
        transform: scale(1.25);
      }

      img {
        transform: scale(1.165);
        overflow: hidden;
      }
    }
  }

  &.page-loading {
    ${EnrollmentCardContent} {
      &::after {
        background: transparent !important;
      }
    }
  }

  ${(props: EnrollmentCardProps) => {
    if (props.$isLoggedInVariant) {
      return css`
        ${FloatingCardContent} {
          position: absolute;
          bottom: 0;
        }

        ${EnrollmentCardContent} {
          justify-content: flex-end;
          padding: 0 ${toRem(24)} ${toRem(24)};
          text-align: left;

          &::after {
            content: '';
            position: absolute;
            inset: 0;
            background: linear-gradient(
              to top,
              var(--color-scrim-100, rgba(0, 0, 0, 0.54)) 56%,
              var(--color-scrim-0, rgba(0, 0, 0, 0)) 82%
            );
            z-index: -1;
            left: 0;
            right: 0;
            bottom: 0;
          }

          a {
            text-align: center;
          }

          .inverse {
            background: none;
          }

          @media ${baseVariables.mediaQuery.lg} {
            &::after {
              background: linear-gradient(
                to top,
                var(--color-scrim-100, rgba(0, 0, 0, 0.54)) 40%,
                var(--color-scrim-0, rgba(0, 0, 0, 0)) 61%
              );
            }
          }
        }

        picture {
          position: relative;
          overflow: hidden;
          border-radius: ${toRem(14)};
        }
      `;
    } else {
      return css`
        ${Title} {
          font-size: ${toRem(25)};
          @media ${baseVariables.mediaQuery.md} {
            font-size: ${toRem(29)};
          }
        }
      `;
    }
  }}
`;
